<template>
  <v-dialog v-model="visible" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="close">
    <v-card class="dps-card">
      <div class="glass-header-eracun">
        <div class="header-pattern"></div>
        <div class="header-title">
          <v-icon color="white" class="mr-2">mdi-file-pdf-box</v-icon>
          <h2 class="headline-eracun white--text">{{ modalTitle }}</h2>
        </div>
        <v-btn icon class="close-btn" @click="close"><v-icon color="white">mdi-close</v-icon></v-btn>
      </div>
      <div class="dps-body">
        <div class="dps-settings">
          <v-text-field
            v-model.number="fontSize"
            type="number"
            min="1"
            max="25"
            label="Veličina fonta (pt)"
            outlined
            dense
            class="filter-select"
          ></v-text-field>
          <div class="orientation-field">
            <div class="orientation-label">Orijentacija</div>
            <v-radio-group v-model="orientation" row hide-details class="mt-0">
              <v-radio value="portrait" color="#3989C9">
                <template v-slot:label>
                  <v-icon size="40">mdi-crop-portrait</v-icon>
                </template>
              </v-radio>
              <v-radio value="landscape" color="#3989C9">
                <template v-slot:label>
                  <v-icon size="40">mdi-crop-landscape</v-icon>
                </template>
              </v-radio>
            </v-radio-group>
          </div>
          <template v-if="scope !== 'single'">
            <v-checkbox v-model="onePerPage" label="Jedna deklaracija po stranici" color="#00838F" hide-details></v-checkbox>
            <v-text-field
              v-if="!onePerPage"
              v-model.number="perPage"
              type="number"
              min="1"
              max="10"
              label="Broj deklaracija po stranici"
              outlined
              dense
              class="filter-select mt-4"
            ></v-text-field>
          </template>
          <v-checkbox v-model="border" label="Obrub oko deklaracije" color="#00838F" hide-details class="mt-4"></v-checkbox>
          <v-radio-group v-if="border" v-model="borderStyle" row class="mt-2">
            <v-radio label="Pravokutni" value="square" color="#3989C9"></v-radio>
            <v-radio label="Zaobljen" value="rounded" color="#3989C9"></v-radio>
          </v-radio-group>
        </div>
        <div class="dps-preview">
          <iframe v-if="previewBlobUrl" :src="previewBlobUrl" class="dps-iframe"></iframe>
          <div v-else class="dps-loading">
            <v-progress-circular indeterminate color="#00BCD4" size="40" />
            <span>Generiram pretpregled...</span>
          </div>
        </div>
        <div class="dps-actions">
          <button class="dps-action dps-action--primary" :disabled="!previewBlobUrl" @click="printPdf">
            <v-icon size="18" color="white" class="mr-2">mdi-printer-outline</v-icon>
            Isprintaj
          </button>
          <button class="dps-action" :disabled="!previewBlobUrl" @click="downloadPdf">
            <v-icon size="18" color="#00BCD4" class="mr-2">mdi-download-outline</v-icon>
            Preuzmi PDF
          </button>
          <button class="dps-action" @click="downloadDocxOutput">
            <v-icon size="18" color="#00BCD4" class="mr-2">mdi-file-word-outline</v-icon>
            Preuzmi DOCX
          </button>
          <div class="dps-spacer"></div>
          <button class="dps-action dps-action--close" @click="close">
            <v-icon size="18" color="#64748B" class="mr-2">mdi-close</v-icon>
            Zatvori
          </button>
        </div>
      </div>
    </v-card>
    <declaration-print-table
      v-if="scope === 'single' && previewItem"
      ref="singleTable"
      :item="previewItem"
      :fields="previewFields"
      table-id="declarationPrintTable-modal"
      :settings="settingsPayload"
    ></declaration-print-table>
    <declaration-catalog-print-table
      v-if="scope !== 'single' && entries.length > 0"
      ref="catalogTable"
      :entries="entries"
      :settings="settingsPayload"
      :document-title="modalTitle"
    ></declaration-catalog-print-table>
  </v-dialog>
</template>
<script>
import state from '@/state'
import { saveCatalogPrintSettings, downloadBlob } from './declarationsService.js'
import DeclarationPrintTable from './DeclarationPrintTable.vue'
import DeclarationCatalogPrintTable from './DeclarationCatalogPrintTable.vue'

const PREVIEW_DEBOUNCE_MS = 500

export default {
  name: 'DeclarationPrintSettingsModal',
  components: { DeclarationPrintTable, DeclarationCatalogPrintTable },
  data: () => ({
    visible: false,
    scope: 'catalog',
    fontSize: 11,
    orientation: 'portrait',
    perPage: 2,
    onePerPage: false,
    border: true,
    borderStyle: 'square',
    previewItem: null,
    previewFields: [],
    entries: [],
    previewBase64: null,
    previewBlobUrl: null,
    debounceTimer: null
  }),
  computed: {
    settingsPayload () {
      return {
        font_size: this.fontSize,
        orientation: this.orientation,
        per_page: this.onePerPage ? 1 : Math.max(1, this.perPage || 1),
        one_per_page: this.onePerPage,
        border: this.border,
        border_style: this.borderStyle
      }
    },
    modalTitle () {
      if (this.scope === 'single') return `Print deklaracije — ${this.previewItem ? this.previewItem.name : ''}`
      if (this.scope === 'selected') return 'Print odabranih deklaracija'
      return 'Knjiga deklaracija'
    },
    pdfFilename () {
      if (this.scope === 'single') return `Deklaracija-${this.previewItem.name}.pdf`
      if (this.scope === 'selected') return 'Odabrane-deklaracije.pdf'
      return 'Knjiga-deklaracija.pdf'
    },
    docxFilename () {
      if (this.scope === 'single') return `Deklaracija-${this.previewItem.name}.docx`
      if (this.scope === 'selected') return 'Odabrane-deklaracije.docx'
      return 'Knjiga-deklaracija.docx'
    }
  },
  watch: {
    fontSize () { this.schedulePreview() },
    orientation () { this.schedulePreview() },
    perPage () { this.schedulePreview() },
    onePerPage () { this.schedulePreview() },
    border () { this.schedulePreview() },
    borderStyle () { this.schedulePreview() }
  },
  methods: {
    open (scope, savedSettings, context) {
      this.scope = scope
      if (savedSettings) {
        this.fontSize = savedSettings.font_size || 11
        this.orientation = savedSettings.orientation || 'portrait'
        this.perPage = savedSettings.per_page || 2
        this.onePerPage = !!savedSettings.one_per_page
        this.border = savedSettings.border !== undefined ? savedSettings.border : true
        this.borderStyle = savedSettings.border_style || 'square'
      } else {
        this.fontSize = 11
        this.orientation = 'portrait'
        this.perPage = 2
        this.onePerPage = false
        this.border = true
        this.borderStyle = 'square'
      }
      if (scope === 'single') {
        this.previewItem = context.item
        this.previewFields = context.fields
        this.entries = []
      } else {
        this.previewItem = null
        this.previewFields = []
        this.entries = context.entries
      }
      this.previewBase64 = null
      this.previewBlobUrl = null
      this.visible = true
      this.regeneratePreview()
    },
    close () {
      this.visible = false
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      if (this.previewBlobUrl) { URL.revokeObjectURL(this.previewBlobUrl); this.previewBlobUrl = null }
      this.previewBase64 = null
    },
    schedulePreview () {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => { this.regeneratePreview() }, PREVIEW_DEBOUNCE_MS)
    },
    async regeneratePreview () {
      await this.$nextTick()
      // jspdf-autotable's useCss:true reads the offscreen table's CSS via
      // getComputedStyle() — right after open() (dialog just mounted), the
      // browser may not have painted/laid out that table yet, so border/style
      // reads as unset. Wait one paint frame past $nextTick to avoid that race.
      await new Promise(resolve => requestAnimationFrame(resolve))
      const ref = this.scope === 'single' ? this.$refs.singleTable : this.$refs.catalogTable
      if (!ref) return
      const base64 = await ref.generate()
      if (!this.visible) return
      this.previewBase64 = base64
      if (this.previewBlobUrl) URL.revokeObjectURL(this.previewBlobUrl)
      this.previewBlobUrl = URL.createObjectURL(this.base64ToBlob(base64))
    },
    base64ToBlob (b64) {
      const bytes = atob(b64)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
      return new Blob([arr], { type: 'application/pdf' })
    },
    async persistSettings () {
      await saveCatalogPrintSettings(state.getCurrentCompany().id, this.settingsPayload)
      this.$emit('saved', this.settingsPayload)
    },
    async printPdf () {
      await this.persistSettings()
      if (!this.previewBlobUrl) return
      const iframe = document.createElement('iframe')
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;'
      iframe.src = this.previewBlobUrl
      document.body.appendChild(iframe)
      iframe.addEventListener('load', () => {
        setTimeout(() => {
          iframe.contentWindow.print()
          setTimeout(() => document.body.removeChild(iframe), 2000)
        }, 300)
      }, { once: true })
    },
    async downloadPdf () {
      await this.persistSettings()
      if (!this.previewBase64) return
      const url = URL.createObjectURL(this.base64ToBlob(this.previewBase64))
      const a = document.createElement('a')
      a.href = url
      a.download = this.pdfFilename
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    async downloadDocxOutput () {
      await this.persistSettings()
      const ref = this.scope === 'single' ? this.$refs.singleTable : this.$refs.catalogTable
      if (!ref) return
      const blob = ref.generateDocx()
      downloadBlob(blob, this.docxFilename)
    }
  }
}
</script>
<style scoped>
.dps-card {
  background: #F5F7FA;
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.glass-header-eracun {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, rgba(0, 179, 190, 0.5) 0%, rgba(57, 137, 201, 0.45) 50%, rgba(97, 84, 164, 0.5) 100%) !important;
  position: relative !important;
  flex-shrink: 0;
}
.header-pattern {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
}
.header-title {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}
.headline-eracun {
  font-size: 20px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  margin: 0 !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.close-btn {
  background: rgba(255, 255, 255, 0.15);
  position: relative;
  z-index: 1;
}
.dps-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.dps-settings {
  width: 300px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #E2E8F0;
  padding: 24px;
  overflow-y: auto;
}
.dps-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow: hidden;
  background: #F1F5F9;
}
.dps-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.10);
}
.dps-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #94A3B8;
  font-size: 13px;
}
.dps-actions {
  width: 240px;
  flex-shrink: 0;
  background: #ffffff;
  border-left: 1px solid #E2E8F0;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 12px;
}
.dps-spacer { flex: 1; }
.dps-action {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1.5px solid #E2E8F0;
  background: #F8FAFC;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  flex-shrink: 0;
}
.dps-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dps-action:hover:not(:disabled) {
  border-color: #00BCD4;
  color: #00BCD4;
  background: rgba(0, 188, 212, 0.05);
}
.dps-action--primary {
  background: #00BCD4 !important;
  border-color: #00BCD4 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 10px rgba(0, 188, 212, 0.30);
}
.dps-action--primary:hover:not(:disabled) {
  background: #0097A7 !important;
  border-color: #0097A7 !important;
  color: #ffffff !important;
}
.dps-action--close {
  border-color: #E2E8F0;
  background: #F8FAFC;
  color: #64748B;
}
.dps-action--close:hover {
  border-color: #94A3B8;
  color: #374151;
  background: #F1F5F9;
}
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: #B0BEC5 !important;
  border-width: 1.5px !important;
  border-radius: 8px !important;
}
.orientation-field {
  margin-top: 16px;
}
.orientation-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
</style>
